import React, { useState } from 'react';
import { SignInButton, UserButton } from '@clerk/react';
import { useIsMobile } from '../hooks/useMediaQuery';
import { T, withAlpha } from '../designTokens';
import { AuthState } from './AuthState';
import CreditBalance from './CreditBalance';
import { CreditCardIcon, ExternalLinkIcon } from './Icons';
import { isSubscriptionTier, type Tier } from '../utils/types';
import type { NavPage } from '../utils/routes';

const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://getprivisy.com';

type NavKey = 'scanner' | 'reports' | 'monitoring';

/**
 * The work pages, in order. Queue is part of Reports (a running scan is a
 * report that has not finished), and Billing is account settings, so both
 * live off the top bar: the Reports page links to the queue, and Billing
 * sits in the account menu and behind the credit chip.
 */
function navItemsFor(tier: Tier | undefined): Array<{ key: NavKey; label: string }> {
  return [
    { key: 'scanner', label: 'Sites' },
    { key: 'reports', label: 'Reports' },
    ...(tier && isSubscriptionTier(tier) ? [{ key: 'monitoring' as const, label: 'Monitoring' }] : []),
  ];
}

/** The nav item a page belongs under; the queue is a view of Reports. */
function activeKey(page: string): NavKey | null {
  if (page === 'scanner' || page === 'reports' || page === 'monitoring') return page;
  return page === 'queue' ? 'reports' : null;
}

// ---------------------------------------------------------------------------
// Header — hamburger menu on mobile
// ---------------------------------------------------------------------------

export default function Header({
  currentPage,
  onNavigate,
  queueCount = 0,
  authEnabled,
  creditBalance,
  creditTier,
  creditLoading,
  creditHistory,
  onBillingClick,
}: {
  currentPage: NavPage | 'terms';
  onNavigate: (page: NavPage) => void;
  queueCount?: number;
  authEnabled: boolean;
  creditBalance?: number | null;
  creditTier?: Tier;
  creditLoading?: boolean;
  /** `hasCreditHistory` from GET /api/credits (#1776). */
  creditHistory?: boolean;
  onBillingClick?: () => void;
}) {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  const navItems = navItemsFor(creditTier);
  const active = activeKey(currentPage);

  const handleNav = (page: NavPage) => {
    onNavigate(page);
    setMenuOpen(false);
  };

  const accountMenu = (avatarSize: string) => (
    <UserButton appearance={{ elements: { avatarBox: { width: avatarSize, height: avatarSize } } }}>
      <UserButton.MenuItems>
        <UserButton.Action label="manageAccount" />
        <UserButton.Action
          label="Billing"
          labelIcon={<CreditCardIcon size={16} />}
          onClick={() => handleNav('billing')}
        />
        <UserButton.Link label="getprivisy.com" labelIcon={<ExternalLinkIcon size={16} />} href={MARKETING_URL} />
        <UserButton.Action label="signOut" />
      </UserButton.MenuItems>
    </UserButton>
  );

  const logoContent = (
    <>
      <div className="logo-icon">
        <span className="logo-mark" aria-hidden="true" />
      </div>
      Privisy
    </>
  );
  const appLogo = (
    <a
      href="#/scanner"
      className="logo"
      onClick={(e) => {
        e.preventDefault();
        handleNav('scanner');
      }}
    >
      {logoContent}
    </a>
  );
  const marketingLogo = (
    <a href={MARKETING_URL} className="logo">
      {logoContent}
    </a>
  );

  const queueBadge = (radius: string) => (
    <span
      aria-label={`${queueCount} in progress`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '18px',
        height: '18px',
        padding: '0 5px',
        borderRadius: radius,
        backgroundColor: T.warning,
        color: T.white,
        fontSize: '12px',
        fontFamily: T.fontMono,
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      {queueCount}
    </span>
  );

  return (
    <header
      className="no-print"
      style={{
        backgroundColor: T.gray900,
        padding: isMobile ? '14px 20px' : '16px 32px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: `1px solid ${T.overlayFaint}`,
        position: 'relative',
        zIndex: 100,
      }}
    >
      {/* Logo: the app's home for anyone with an account, like every
          product's. A signed-out visitor (a shared report) has no app home,
          so theirs still leads back to the marketing site; signed-in users
          reach that from the account menu. */}
      {authEnabled ? (
        <AuthState signedIn={appLogo} signedOut={marketingLogo} loading={marketingLogo} />
      ) : (
        appLogo
      )}

      {isMobile ? (
        // Mobile: hamburger button + dropdown
        <>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {authEnabled && (
              <AuthState
                loading={(
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: T.overlayFaint }} />
                )}
                signedOut={<></>}
                signedIn={<div>{accountMenu('32px')}</div>}
              />
            )}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              style={{
                background: 'none',
                border: `1px solid ${withAlpha(T.white, 0.2)}`,
                borderRadius: '8px',
                color: T.white,
                fontSize: '20px',
                cursor: 'pointer',
                padding: '6px 10px',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>

          {/* Dropdown overlay */}
          {menuOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: T.gray800,
                borderTop: `1px solid ${T.overlayLine}`,
                boxShadow: `0 8px 16px ${withAlpha(T.black, 0.3)}`,
                zIndex: 200,
                padding: '8px 0',
              }}
            >
              {[...navItems, { key: 'billing' as const, label: 'Billing' }].map(({ key, label }) => {
                const current = key === active || (key === 'billing' && currentPage === 'billing');
                return (
                  <button
                    key={key}
                    onClick={() => handleNav(key)}
                    aria-current={current ? 'page' : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '14px 24px',
                      fontSize: '15px',
                      fontWeight: current ? 600 : 400,
                      color: current ? T.white : T.gray400,
                      backgroundColor: current ? withAlpha(T.cobalt600, 0.2) : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      gap: '10px',
                      minHeight: '44px',
                    }}
                  >
                    {label}
                    {key === 'reports' && queueCount > 0 && queueBadge('10px')}
                  </button>
                );
              })}
              <a
                href={MARKETING_URL}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 24px',
                  minHeight: '44px',
                  boxSizing: 'border-box',
                  fontSize: '15px',
                  color: T.gray400,
                  textDecoration: 'none',
                }}
              >
                getprivisy.com <ExternalLinkIcon size={14} />
              </a>
              {/* Credit balance in mobile menu — a signed-in affordance:
                  anonymous visitors have no account for a balance to describe */}
              {(() => {
                const chip = (
                  <div style={{ padding: '12px 24px', borderTop: `1px solid ${T.overlayLine}`, marginTop: '4px' }}>
                    <CreditBalance
                      balance={creditBalance ?? null}
                      tier={creditTier ?? 'free'}
                      loading={creditLoading ?? true}
                      hasCreditHistory={creditHistory}
                      onClick={onBillingClick}
                    />
                  </div>
                );
                return authEnabled ? <AuthState signedIn={chip} signedOut={<></>} loading={<></>} /> : chip;
              })()}
              {authEnabled && (
                <div style={{ padding: '12px 24px', borderTop: `1px solid ${T.overlayLine}`, marginTop: '4px' }}>
                  <AuthState
                    signedOut={(
                      <SignInButton mode="modal">
                        <button
                          style={{
                            width: '100%',
                            padding: '12px',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: T.white,
                            backgroundColor: T.cobalt500,
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            minHeight: '44px',
                          }}
                        >
                          Sign In
                        </button>
                      </SignInButton>
                    )}
                    signedIn={<></>}
                  />
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        // Desktop: full nav bar
        <nav style={{ marginLeft: 'auto', display: 'flex', gap: '4px', alignItems: 'center' }}>
          {navItems.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => onNavigate(key)}
              aria-current={active === key ? 'page' : undefined}
              style={{
                padding: '7px 14px',
                fontSize: '13px',
                color: active === key ? T.gray100 : T.gray400,
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: active === key ? `2px solid ${T.cobalt600}` : '2px solid transparent',
                borderRadius: 0,
                cursor: 'pointer',
                fontWeight: active === key ? 600 : 400,
                transition: 'color 0.15s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
              onMouseEnter={(e) => { if (active !== key) e.currentTarget.style.color = T.gray300; }}
              onMouseLeave={(e) => { if (active !== key) e.currentTarget.style.color = T.gray400; }}
            >
              {label}
              {key === 'reports' && queueCount > 0 && queueBadge('9999px')}
            </button>
          ))}

          {/* Credit balance widget — signed-in only (see mobile note above) */}
          {(() => {
            const chip = (
              <div style={{ marginLeft: '8px' }}>
                <CreditBalance
                  balance={creditBalance ?? null}
                  tier={creditTier ?? 'free'}
                  loading={creditLoading ?? true}
                  hasCreditHistory={creditHistory}
                  onClick={onBillingClick}
                />
              </div>
            );
            return authEnabled ? <AuthState signedIn={chip} signedOut={<></>} loading={<></>} /> : chip;
          })()}

          {authEnabled && (
            <AuthState
              loading={(
                <div style={{ marginLeft: '12px', width: '60px', height: '32px', borderRadius: '6px', backgroundColor: T.overlayFaint }} />
              )}
              signedOut={(
                <SignInButton mode="modal">
                  <button
                    style={{
                      marginLeft: '12px',
                      padding: '7px 16px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: T.white,
                      backgroundColor: T.cobalt600,
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = T.cobalt700; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = T.cobalt600; }}
                  >
                    Sign In
                  </button>
                </SignInButton>
              )}
              signedIn={<div style={{ marginLeft: '8px' }}>{accountMenu('32px')}</div>}
            />
          )}
        </nav>
      )}
    </header>
  );
}
